import React, { useState } from 'react';
import { Trash2, ChevronUp, ChevronDown, Upload, Edit3, User, Crop } from 'lucide-react';
import type { Area } from 'react-easy-crop';
import { ImageCropModal, type CropData } from '../shared/ImageCropModal';
import { getCroppedImageStyles, hasCropApplied } from '../shared/cropUtils';

interface SocialLinks {
  linkedin?: string;
  twitter?: string;
  email?: string;
  website?: string;
}

interface TeamMember {
  id?: string;
  name: string;
  position: string;
  bio: string;
  imageUrl?: string;
  cloudfrontUrl?: string;
  mediaId?: string;
  imageAlt?: string;
  socialLinks?: SocialLinks;
  order: number;
  crop?: CropData;
}

interface TeamMemberCardProps {
  member: TeamMember;
  index: number;
  totalMembers: number;
  onUpdate: (updates: Partial<TeamMember>) => void;
  onRemove: () => void;
  onMove: (direction: 'up' | 'down') => void;
  onOpenImageUpload: (memberId: string, memberName: string) => void;
}

export const TeamMemberCard: React.FC<TeamMemberCardProps> = ({
  member,
  index,
  totalMembers,
  onUpdate,
  onRemove,
  onMove,
  onOpenImageUpload,
}) => (
  <div className="border border-gray-200 rounded-lg p-6">
    <div className="flex items-center justify-between mb-4">
      <h4 className="text-md font-medium text-gray-800">
        {member.name || `Team Member ${index + 1}`}
      </h4>
      <div className="flex items-center space-x-2">
        <button
          type="button"
          onClick={() => onMove('up')}
          disabled={index === 0}
          className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
        >
          <ChevronUp size={16} />
        </button>
        <button
          type="button"
          onClick={() => onMove('down')}
          disabled={index === totalMembers - 1}
          className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
        >
          <ChevronDown size={16} />
        </button>
        <button type="button" onClick={onRemove} className="p-1 text-red-600 hover:text-red-700">
          <Trash2 size={16} />
        </button>
      </div>
    </div>

    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
        <input
          type="text"
          value={member.name || ''}
          onChange={e => onUpdate({ name: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          placeholder="Full name (optional)"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Position/Title</label>
        <input
          type="text"
          value={member.position || ''}
          onChange={e => onUpdate({ position: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          placeholder="Job title or role (optional)"
        />
      </div>
    </div>

    <div className="mt-4">
      <label className="block text-sm font-medium text-gray-700 mb-2">Bio</label>
      <textarea
        value={member.bio || ''}
        onChange={e => onUpdate({ bio: e.target.value })}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        rows={3}
        placeholder="Brief biography or description..."
      />
    </div>

    {/* Photo Section */}
    <PhotoSection member={member} onOpenImageUpload={onOpenImageUpload} onUpdate={onUpdate} />

    {/* Social Links */}
    <SocialLinksSection member={member} onUpdate={onUpdate} />
  </div>
);

interface PhotoSectionProps {
  member: TeamMember;
  onOpenImageUpload: (memberId: string, memberName: string) => void;
  onUpdate: (updates: Partial<TeamMember>) => void;
}

const PhotoSection: React.FC<PhotoSectionProps> = ({ member, onOpenImageUpload, onUpdate }) => {
  const [showCropModal, setShowCropModal] = useState(false);
  const imageUrl = member.cloudfrontUrl || member.imageUrl;
  const hasCrop = hasCropApplied(member.crop);

  const handleCropComplete = (cropData: CropData, _croppedAreaPixels: Area) => {
    onUpdate({ crop: cropData });
    setShowCropModal(false);
  };

  return (
    <div className="mt-4">
      <label className="block text-sm font-medium text-gray-700 mb-2">Photo</label>
      <div className="flex items-center space-x-4">
        {imageUrl ? (
          <div className="relative">
            {hasCrop ? (
              <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-gray-200 relative">
                <img
                  src={imageUrl}
                  alt={member.imageAlt || member.name || 'Team member'}
                  style={getCroppedImageStyles(member.crop)}
                />
              </div>
            ) : (
              <img
                src={imageUrl}
                alt={member.imageAlt || member.name || 'Team member'}
                className="w-16 h-16 rounded-full object-cover border-2 border-gray-200"
              />
            )}
            <button
              type="button"
              onClick={() => onOpenImageUpload(member.id!, member.name || 'Team Member')}
              className="absolute -top-1 -right-1 p-1 bg-primary-500 text-white rounded-full hover:bg-primary-600 transition-colors"
              title="Change photo"
            >
              <Edit3 size={12} />
            </button>
          </div>
        ) : (
          <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center border-2 border-gray-200">
            <User size={24} className="text-gray-400" />
          </div>
        )}
        <div className="flex flex-col space-y-2">
          <button
            type="button"
            onClick={() => onOpenImageUpload(member.id!, member.name || 'Team Member')}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <Upload size={16} className="mr-2" />
            {imageUrl ? 'Change Photo' : 'Upload Photo'}
          </button>
          {imageUrl && (
            <button
              type="button"
              onClick={() => setShowCropModal(true)}
              className={`inline-flex items-center px-3 py-2 border shadow-sm text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${
                hasCrop
                  ? 'border-primary-500 text-primary-700 bg-primary-50 hover:bg-primary-100'
                  : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
              }`}
            >
              <Crop size={16} className="mr-2" />
              {hasCrop ? 'Edit Crop' : 'Crop Image'}
            </button>
          )}
        </div>
      </div>
      {imageUrl && (
        <div className="mt-3">
          <label className="block text-sm font-medium text-gray-700 mb-1">Image Alt Text</label>
          <input
            type="text"
            value={member.imageAlt || ''}
            onChange={e => onUpdate({ imageAlt: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            placeholder={member.name ? `${member.name} profile photo` : 'Team member photo'}
          />
          <p className="mt-1 text-xs text-gray-500">
            Describe the image for accessibility (e.g., &quot;John Smith headshot&quot;)
          </p>
        </div>
      )}

      {/* Crop Modal */}
      {imageUrl && (
        <ImageCropModal
          isOpen={showCropModal}
          imageUrl={imageUrl}
          initialCrop={member.crop}
          lockedAspect={1}
          onClose={() => setShowCropModal(false)}
          onApply={handleCropComplete}
        />
      )}
    </div>
  );
};

interface SocialLinksSectionProps {
  member: TeamMember;
  onUpdate: (updates: Partial<TeamMember>) => void;
}

const SocialLinksSection: React.FC<SocialLinksSectionProps> = ({ member, onUpdate }) => (
  <div className="mt-4">
    <label className="block text-sm font-medium text-gray-700 mb-2">Social Links</label>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <input
        type="url"
        value={member.socialLinks?.linkedin || ''}
        onChange={e =>
          onUpdate({ socialLinks: { ...member.socialLinks, linkedin: e.target.value } })
        }
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        placeholder="LinkedIn URL"
      />
      <input
        type="url"
        value={member.socialLinks?.twitter || ''}
        onChange={e =>
          onUpdate({ socialLinks: { ...member.socialLinks, twitter: e.target.value } })
        }
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        placeholder="Twitter URL"
      />
      <input
        type="email"
        value={member.socialLinks?.email || ''}
        onChange={e => onUpdate({ socialLinks: { ...member.socialLinks, email: e.target.value } })}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        placeholder="Email address"
      />
      <input
        type="url"
        value={member.socialLinks?.website || ''}
        onChange={e =>
          onUpdate({ socialLinks: { ...member.socialLinks, website: e.target.value } })
        }
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        placeholder="Website URL"
      />
    </div>
  </div>
);

export type { TeamMember, SocialLinks };
